import {
  applyAccountIdFromSearch,
  getAccountId,
  restoreAccountId,
  setAccountId,
} from "@/lib/auth/account";
import { clearSignedOut, markHadSession } from "@/lib/auth/sessionMarker";
import { apiGet } from "@/lib/http/client";
import type { AppAccountStatus } from "@/types/api";

export type WebSession = {
  stripe_account_id: string;
  expires_at?: string;
  app_status?: AppAccountStatus;
};

export type BootstrapAuthResult = "ready" | "guest" | "app_uninstalled";

export function isAppUninstalledStatus(status: AppAccountStatus | undefined): boolean {
  return status === "uninstalled";
}

/** 启动时从 Web 会话 Cookie（/auth/session）恢复账户。 */
export async function bootstrapAuth(): Promise<BootstrapAuthResult> {
  if (typeof window !== "undefined") {
    applyAccountIdFromSearch(window.location.search);
  }
  restoreAccountId();
  try {
    const session = await fetchSession();
    if (!session?.stripe_account_id?.startsWith("acct_")) {
      if (!hasAuthenticatedAccount()) {
        return "guest";
      }
      markHadSession();
      return "ready";
    }
    if (isAppUninstalledStatus(session.app_status)) {
      setAccountId(null);
      return "app_uninstalled";
    }
    setAccountId(session.stripe_account_id);
    markHadSession();
    clearSignedOut();
    return "ready";
  } catch {
    if (!hasAuthenticatedAccount()) {
      return "guest";
    }
    markHadSession();
    return "ready";
  }
}

export function hasAuthenticatedAccount(): boolean {
  restoreAccountId();
  return getAccountId()?.startsWith("acct_") ?? false;
}

export async function fetchSession(): Promise<WebSession | null> {
  const data = await apiGet<WebSession | null>("/auth/session");
  if (!data?.stripe_account_id?.startsWith("acct_")) {
    return null;
  }
  return data;
}
